import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Pill, Stethoscope, CalendarDays, Download, ChevronRight, X, UserRound } from "lucide-react";
import StatusBadge from "../components/StatusBadge";
import { getPatientPrescriptions, getPatientReports, getPrescriptionById, downloadPrescriptionFile, checkPrescriptionPortalPhone, signInToPrescriptionPortal, setPrescriptionPortalPassword, requestPortalPasswordResetOtp, verifyPortalPasswordResetOtp, resetPortalPassword } from "../lib/api";
import { Reveal } from "../components/Motion";
import { toast } from "sonner";


export default function Prescriptions() {
  const [prescriptions, setPrescriptions] = useState([]);
  const [active, setActive] = useState(null);
  const [sortOrder, setSortOrder] = useState("NEWEST");
  const [phone, setPhone] = useState("");
  const [phoneStatus, setPhoneStatus] = useState("");
  const [phoneStatusMessage, setPhoneStatusMessage] = useState("");
  const [password, setPassword] = useState("");
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [portalAuthenticated, setPortalAuthenticated] = useState(false);
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [forgotOtpSent, setForgotOtpSent] = useState(false);
  const [forgotOtpVerified, setForgotOtpVerified] = useState(false);
  const [forgotOtp, setForgotOtp] = useState("");
  const [reports, setReports] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [portalPatients, setPortalPatients] = useState([]);
  const [patientPickerOpen, setPatientPickerOpen] = useState(false);
  const [pendingPortalAction, setPendingPortalAction] = useState("");
  const [selectedPortalPatient, setSelectedPortalPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { id: routePrescriptionId } = useParams();
  const sortedPrescriptions = useMemo(() => sortPrescriptions(prescriptions, sortOrder), [prescriptions, sortOrder]);
  const rx = sortedPrescriptions.find((p) => p.id === active) || null;
  const phoneValid = phone.length === 10;

  const normalizePhone = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length === 12 && digits.startsWith("91")) {
      return digits.slice(2);
    }
    return digits.slice(0, 10);
  };


  const handlePhoneChange = (value) => {
    setError("");
    setPassword("");
    setPhoneStatus("");
    setPhoneStatusMessage("");
    setPhone(normalizePhone(value));
  };

  useEffect(() => {
    if (!phoneValid) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPhoneStatus("CHECKING");
      setPhoneStatusMessage("Checking your mobile number...");
      try {
        const result = await checkPrescriptionPortalPhone(phone);
        if (!cancelled) {
          setPhoneStatus(result?.status || "");
          setPhoneStatusMessage(result?.message || "");
        }
      } catch (e) {
        if (!cancelled) {
          setPhoneStatus("ERROR");
          setPhoneStatusMessage(e?.response?.data?.message || "Unable to check this mobile number. Please try again.");
        }
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [phone, phoneValid]);


  const signIn = async () => {
    if (!phoneValid) return setError("Enter a valid 10-digit mobile number");
    if (!password.trim()) return setError("Enter your password");
    setLoading(true); setError("");
    try {
      const result = await signInToPrescriptionPortal(phone, password);
      const patients = Array.isArray(result?.patients) ? result.patients : [];
      setPortalPatients(patients);
      setSelectedPortalPatient(patients.length === 1 ? patients[0] : null);
      if (result?.firstLogin) {
        setTemporaryPassword(password);
        setNeedsPasswordSetup(true);
        setPortalAuthenticated(false);
        toast.success("Create your personal password to continue.");
      } else {
        setPortalAuthenticated(true);
        setNeedsPasswordSetup(false);
        toast.success("Signed in successfully.");
      }
      setReports(null);
      localStorage.setItem("lastPatientPhone", phone);
    } catch (e) {
      setError(e?.response?.data?.message || "Invalid mobile number or password.");
      setPortalAuthenticated(false);
      setNeedsPasswordSetup(false);
      setPortalPatients([]);
    } finally { setLoading(false); }
  };

  const createPassword = async () => {
    if (newPassword.length < 8) return setError("Password must be at least 8 characters");
    if (newPassword !== confirmPassword) return setError("Passwords do not match");
    setLoading(true); setError("");
    try {
      const result = await setPrescriptionPortalPassword(phone, temporaryPassword, newPassword, confirmPassword);
      const patients = Array.isArray(result?.patients) ? result.patients : portalPatients;
      setPortalPatients(patients);
      setSelectedPortalPatient(patients.length === 1 ? patients[0] : null);
      setPassword(newPassword);
      setTemporaryPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setNeedsPasswordSetup(false);
      setPortalAuthenticated(true);
      toast.success("Password created. You are signed in.");
    }
    catch (e) { setError(e?.response?.data?.message || "Unable to create password."); }
    finally { setLoading(false); }
  };

  const startForgotPassword = async () => {
    if (!phoneValid) return setError("Enter a valid 10-digit mobile number");
    setLoading(true); setError("");
    try {
      await requestPortalPasswordResetOtp(phone);
      setForgotOtpSent(true);
      setForgotOtpVerified(false);
      toast.success("OTP sent to your WhatsApp number.");
    } catch (e) { setError(e?.response?.data?.message || "Unable to send OTP."); }
    finally { setLoading(false); }
  };

  const verifyForgotPasswordOtp = async () => {
    if (forgotOtp.length !== 6) return setError("Enter the 6-digit OTP");
    setLoading(true); setError("");
    try {
      await verifyPortalPasswordResetOtp(phone, forgotOtp);
      setForgotOtpSent(false);
      setForgotOtp("");
      setForgotOtpVerified(true);
      toast.success("OTP verified. Create a new password.");
    } catch (e) { setError(e?.response?.data?.message || "Invalid OTP."); }
    finally { setLoading(false); }
  };

  const finishForgotPassword = async () => {
    if (newPassword.length < 8) return setError("Password must be at least 8 characters");
    if (newPassword !== confirmPassword) return setError("Passwords do not match");
    setLoading(true); setError("");
    try {
      await resetPortalPassword(phone, newPassword, confirmPassword);
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setForgotPassword(false);
      setForgotOtpVerified(false);
      setPhoneStatus("PASSWORD");
      setPhoneStatusMessage("Password saved. Enter your new password to sign in.");
      setPortalAuthenticated(false);
      toast.success("Password saved. Please sign in with your new password.");
    } catch (e) { setError(e?.response?.data?.message || "Unable to reset password."); }
    finally { setLoading(false); }
  };

  const viewReports = async (patientId) => {
    setReportsLoading(true); setError("");
    try { const data = await getPatientReports(phone, patientId, password); setReports(Array.isArray(data) ? data : []); }
    catch (e) { setError(e?.response?.data?.message || "Unable to load reports."); }
    finally { setReportsLoading(false); }
  };

  const openPatientPicker = (action) => {
    if (!portalPatients.length) return setError("No patient profile was found for this phone number.");
    setPendingPortalAction(action);
    setSelectedPortalPatient(portalPatients.length === 1 ? portalPatients[0] : null);
    setPatientPickerOpen(true);
  };

  const continuePatientAction = async () => {
    if (!selectedPortalPatient) return setError("Select a patient to continue.");
    setPatientPickerOpen(false);
    if (pendingPortalAction === "reports") await viewReports(selectedPortalPatient.id);
    else await unlockVault(selectedPortalPatient.id);
  };

  const resolveDoctorName = (prescription) => {
    if (!prescription) return "Unknown";
    return prescription.doctorName || prescription.doctor?.doctorName || prescription.doctor?.name || "Unknown";
  };

  const resolvePatientInfo = (prescription) => {
    if (!prescription) return "Patient";
    const name = prescription.patientName || prescription.patient?.patientName || "Patient";
    const age = prescription.age || prescription.patient?.age;
    const gender = prescription.gender || prescription.patient?.gender;
    const label = `${name}`;
    const meta = [age ? `${age} yrs` : null, gender ? `${gender}` : null].filter(Boolean).join(" · ");
    return meta ? `${label} · ${meta}` : label;
  };


  function sortPrescriptions(list, order) {
    return [...list].sort((a, b) => {
      const aDate = new Date(a.date || a.createdAt || 0).getTime();
      const bDate = new Date(b.date || b.createdAt || 0).getTime();
      return order === "NEWEST" ? bDate - aDate : aDate - bDate;
    });
  }


  async function unlockVault(patientId) {
    setError("");
    if (!phoneValid) {
      setError("Enter a valid 10-digit mobile number");
      return;
    }
    if (!portalAuthenticated) {
      setError("Sign in to your Prescription Portal account");
      return;
    }
    setLoading(true);
    try {
      const data = await getPatientPrescriptions(phone, patientId, password);
      const list = Array.isArray(data) ? data : [];
      setPrescriptions(list);
      setActive(list.length ? list[0].id : null);
      if (!list.length) toast.success("No prescriptions found for this phone number");
    } catch (e) {
      console.error(e);
      const serverMessage = e?.response?.data?.message || e?.response?.data?.error || e?.message;
      toast.error(serverMessage || "Failed to fetch prescriptions");
    } finally {
      setLoading(false);
    }
  }


  const [showModal, setShowModal] = useState(false);
  const [modalRx, setModalRx] = useState(null);


  async function openModalWithId(id) {
    try {
      setLoading(true);
      const resp = await getPrescriptionById(id, phone, password);
      const prescription = resp?.prescription || resp;
      const doctor = resp?.doctor || prescription?.doctor;
      const patient = resp?.patient || prescription?.patient;
      setModalRx({
        ...prescription,
        doctor,
        patient,
        doctorName: prescription?.doctorName || doctor?.doctorName || doctor?.name,
        doctorPhone: prescription?.doctorPhone || doctor?.doctorPhone || doctor?.phone,
        patientName: prescription?.patientName || patient?.patientName,
        age: prescription?.age || patient?.age,
        gender: prescription?.gender || patient?.gender,
      });
      setActive(id);
      setShowModal(true);
    } catch (e) {
      console.error("Failed to load prescription details", e);
      const serverMessage = e?.response?.data?.message || e?.message;
      toast.error(serverMessage || "Failed to load details");
    } finally {
      setLoading(false);
    }
  }


  async function downloadPrescription(id) {
    try {
      setLoading(true);
      const resp = await getPrescriptionById(id, phone, password);
      const pres = resp?.prescription || resp;
      const url = pres?.imageUrl;
      if (!url) {
        toast.error("No downloadable file available for this prescription");
        return;
      }
      const blob = await downloadPrescriptionFile(id, phone, password);
      const fileName = `prescription-${id}.pdf`;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success("Download started");
    } catch (e) {
      console.error("Download failed", e);
      const serverMessage = e?.response?.data?.message || e?.message;
      toast.error(serverMessage || "Download failed");
    } finally {
      setLoading(false);
    }
  }


  useEffect(() => {
    if (!routePrescriptionId) {
      setPhone("");
      setPassword("");
      setPortalAuthenticated(false);
      setNeedsPasswordSetup(false);
      return;
    }

    const directPrescriptionId = Number(routePrescriptionId);
    if (!Number.isFinite(directPrescriptionId) || directPrescriptionId <= 0) {
      return;
    }

    if (!portalAuthenticated) {
      setError("Sign in to your Prescription Portal account before opening this prescription.");
      return;
    }

    (async () => {
      try {
        await openModalWithId(directPrescriptionId);
      } catch (err) {
        console.error("Direct prescription open failed", err);
      }
    })();
  }, [routePrescriptionId]);


  return (
    <div className="relative min-h-screen pt-28 pb-24">
      <div className="glow-teal pointer-events-none absolute top-24 right-0 h-[380px] w-[520px] blur-[120px] opacity-40" />
      <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">My Health</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-medium text-foreground leading-tight">Your health, organised.</h1>
          <p className="mt-3 text-muted-foreground text-lg">Prescriptions, reports and clinical advice — organised and always within reach.</p>
        </Reveal>


        <div className="mt-10 space-y-6">
          <div className="space-y-3">
            {prescriptions.length === 0 ? (
              <div className="p-6">
                {!portalAuthenticated && !needsPasswordSetup && !forgotPassword ? (
                  <>
                    <p className="text-sm text-muted-foreground/70 mb-4">Enter your mobile number to continue.</p>
                    <label htmlFor="patient-phone" className="block text-sm font-medium text-muted-foreground/80 mb-2">Registered mobile number</label>
                    <div className="relative mb-4 max-w-xl"><span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">+91</span><input id="patient-phone" value={phone} onChange={(e) => handlePhoneChange(e.target.value)} placeholder="Enter 10-digit mobile number" inputMode="numeric" autoComplete="tel" maxLength={10} aria-label="Enter registered 10-digit mobile number" className="input w-full rounded-xl border border-border bg-transparent p-3 pl-14 text-foreground" /></div>
                    {phoneStatusMessage && <p className={`mb-4 text-sm ${phoneStatus === "ERROR" || phoneStatus === "NOT_FOUND" ? "text-red-500" : "text-muted-foreground"}`}>{phoneStatusMessage}</p>}
                    {(phoneStatus === "PASSWORD" || phoneStatus === "TEMPORARY_PASSWORD") && <>
                      <label htmlFor="portal-password" className="block text-sm font-medium text-muted-foreground/80 mb-2">{phoneStatus === "TEMPORARY_PASSWORD" ? "Temporary Password" : "Saved Password"}</label>
                      <input id="portal-password" type="password" value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }} placeholder={phoneStatus === "TEMPORARY_PASSWORD" ? "Enter the password sent on WhatsApp" : "Enter your saved password"} autoComplete="current-password" className="input w-full max-w-xl p-3 rounded-xl border border-border bg-transparent text-foreground" />
                    </>}
                    <div className="mt-5 grid max-w-xl gap-2.5">
                      {(phoneStatus === "PASSWORD" || phoneStatus === "TEMPORARY_PASSWORD") && <button onClick={signIn} disabled={loading || !password.trim()} className="inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-45">{loading ? "Signing in..." : "Sign In"}</button>}
                      {(phoneStatus === "PASSWORD" || phoneStatus === "TEMPORARY_PASSWORD_EXPIRED") && <button onClick={() => { setError(""); setForgotPassword(true); setForgotOtpSent(false); setForgotOtpVerified(false); }} disabled={loading} className="inline-flex min-h-10 w-full items-center justify-center rounded-xl border border-primary/30 bg-primary/10 px-4 text-sm font-semibold text-primary transition-colors hover:bg-primary/15">Forgot Password?</button>}
                    </div>
                  </>
                ) : forgotPassword && !forgotOtpSent && !forgotOtpVerified ? (
                  <>
                    <h2 className="text-xl font-semibold text-foreground">Reset Your Password</h2>
                    <p className="mt-2 text-sm text-muted-foreground">We will send a verification code to your registered WhatsApp number.</p>
                    <label htmlFor="forgot-phone" className="mt-5 block text-sm font-medium text-muted-foreground/80 mb-2">Mobile Number</label>
                    <div className="relative max-w-xl"><span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">+91</span><input id="forgot-phone" value={phone} onChange={(e) => handlePhoneChange(e.target.value)} placeholder="Enter 10-digit mobile number" inputMode="numeric" autoComplete="tel" maxLength={10} className="input w-full rounded-xl border border-border bg-transparent p-3 pl-14 text-foreground" /></div>
                    <button onClick={startForgotPassword} disabled={loading || !phoneValid} className="mt-5 inline-flex min-h-11 w-full max-w-xl items-center justify-center rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-45">{loading ? "Sending OTP..." : "Send OTP"}</button>
                    <button onClick={() => { setForgotPassword(false); setError(""); }} disabled={loading} className="mt-2 inline-flex min-h-10 w-full max-w-xl items-center justify-center rounded-xl border border-border px-4 text-sm font-semibold text-muted-foreground">Back to Sign In</button>
                  </>
                ) : forgotOtpSent ? (
                  <>
                    <h2 className="text-xl font-semibold text-foreground">Verify OTP</h2>
                    <p className="mt-2 text-sm text-muted-foreground">Enter the 6-digit OTP sent to your WhatsApp number.</p>
                    <input value={forgotOtp} onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Enter 6-digit OTP" inputMode="numeric" maxLength={6} autoComplete="one-time-code" className="input mt-5 w-full max-w-xl rounded-xl border border-border bg-transparent p-3 text-foreground" />
                    <button onClick={verifyForgotPasswordOtp} disabled={loading || forgotOtp.length !== 6} className="mt-4 inline-flex min-h-11 w-full max-w-xl items-center justify-center rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-45">{loading ? "Verifying..." : "Verify OTP"}</button>
                    <button onClick={startForgotPassword} disabled={loading} className="mt-2 inline-flex min-h-10 w-full max-w-xl items-center justify-center rounded-xl border border-border px-4 text-sm font-semibold text-muted-foreground">Resend OTP</button>
                  </>
                ) : forgotPassword ? (
                  <>
                    <h2 className="text-xl font-semibold text-foreground">Create New Password</h2>
                    <label htmlFor="forgot-new-password" className="mt-5 block text-sm font-medium text-muted-foreground/80 mb-2">New Password</label>
                    <input id="forgot-new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Create a password (8+ characters)" autoComplete="new-password" className="input w-full max-w-xl p-3 rounded-xl border border-border bg-transparent text-foreground" />
                    <label htmlFor="forgot-confirm-password" className="mt-4 block text-sm font-medium text-muted-foreground/80 mb-2">Confirm Password</label>
                    <input id="forgot-confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter your password" autoComplete="new-password" className="input w-full max-w-xl p-3 rounded-xl border border-border bg-transparent text-foreground" />
                    <button onClick={finishForgotPassword} disabled={loading || newPassword.length < 8 || newPassword !== confirmPassword} className="mt-5 inline-flex min-h-11 w-full max-w-xl items-center justify-center rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-45">{loading ? "Saving..." : "Save New Password"}</button>
                  </>
                ) : needsPasswordSetup ? (
                  <>
                    <h2 className="text-xl font-semibold text-foreground">Create Your Password</h2>
                    <p className="mt-2 text-sm text-muted-foreground">You're signing in for the first time. Please create your own password to securely access your prescriptions in the future.</p>
                    <label htmlFor="new-portal-password" className="mt-5 block text-sm font-medium text-muted-foreground/80 mb-2">New Password</label>
                    <input id="new-portal-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Create a password (8+ characters)" autoComplete="new-password" className="input w-full max-w-xl p-3 rounded-xl border border-border bg-transparent text-foreground" />
                    <label htmlFor="confirm-portal-password" className="mt-4 block text-sm font-medium text-muted-foreground/80 mb-2">Confirm Password</label>
                    <input id="confirm-portal-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter your password" autoComplete="new-password" className="input w-full max-w-xl p-3 rounded-xl border border-border bg-transparent text-foreground" />
                    <button onClick={createPassword} disabled={loading || newPassword.length < 8 || newPassword !== confirmPassword} className="mt-5 inline-flex min-h-11 w-full max-w-xl items-center justify-center rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-45">{loading ? "Setting password..." : "Set Password"}</button>
                  </>
                ) : (
                  <div className="grid max-w-xl gap-2.5 pt-2">
                    <button onClick={() => openPatientPicker("prescriptions")} disabled={loading || !portalPatients.length} className="inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-45">View prescriptions</button>
                    <button onClick={() => openPatientPicker("reports")} disabled={reportsLoading || !portalPatients.length} className="inline-flex min-h-11 w-full items-center justify-center rounded-xl border border-primary/30 bg-primary/10 px-4 text-sm font-semibold text-primary transition-all hover:-translate-y-0.5 hover:bg-primary/15 disabled:cursor-not-allowed disabled:opacity-45">{reportsLoading ? "Loading reports..." : "View reports"}</button>
                  </div>
                )}
                {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
                {reports && <div className="mt-5 rounded-2xl border border-border bg-muted/30 p-4"><div className="mb-3 flex items-center justify-between"><strong className="text-sm text-foreground">Reports</strong><span className="text-xs text-muted-foreground">{reports.length} found</span></div>{reports.length ? <div className="space-y-2">{reports.map((report) => <a key={report.id} href={report.fileUrl} target="_blank" rel="noreferrer" className="flex items-center justify-between rounded-xl border border-border bg-background/60 px-3 py-2 text-sm text-foreground transition hover:border-primary"><span className="truncate">{report.fileName || report.reportName || "Clinical report"}</span><span className="ml-3 shrink-0 text-xs font-semibold text-primary">Open</span></a>)}</div> : <p className="text-sm text-muted-foreground">No reports found for this phone number.</p>}</div>}
              </div>
            ) : sortedPrescriptions.map((p, i) => {
              const sel = p.id === active;
              return (
                <Reveal key={p.id} delay={i * 0.06}>
                  <button data-testid={`rx-item-${p.id}`} onClick={() => openModalWithId(p.id)}
                    className={`w-full text-left rounded-3xl p-6 border transition-colors ${sel ? "glass border-primary/40" : "bg-muted/40 border-border hover:border-border"}`}>
                    <div className="flex items-center justify-between">
                      <div className="h-11 w-11 rounded-2xl aurora-gradient grid place-items-center text-white"><FileText className="h-5 w-5" /></div>
                      <StatusBadge status={p.status} />
                    </div>
                    <h3 className="mt-4 font-display text-xl text-foreground">{p.diagnosis}</h3>
                    <p className="mt-1 text-sm text-muted-foreground/70">{p.doctorName} · {p.date}</p>
                    <span className={`mt-3 inline-flex items-center gap-1 text-sm font-medium ${sel ? "text-primary" : "text-muted-foreground/70"}`}>
                      View details <ChevronRight className="h-4 w-4" />
                    </span>
                  </button>
                </Reveal>
              );
            })}
          </div>


          {prescriptions.length > 0 && (
            <div className="space-y-4">
              <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm text-muted-foreground/70">Sort by</p>
                  <div className="inline-flex rounded-full border border-border bg-muted/40 p-1">
                    <button
                      onClick={() => setSortOrder("NEWEST")}
                      className={`px-4 py-2 rounded-full transition ${sortOrder === "NEWEST" ? "bg-primary text-white" : "text-muted-foreground"}`}>
                      Newest first
                    </button>
                    <button
                      onClick={() => setSortOrder("OLDEST")}
                      className={`px-4 py-2 rounded-full transition ${sortOrder === "OLDEST" ? "bg-primary text-white" : "text-muted-foreground"}`}>
                      Oldest first
                    </button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground/70">{prescriptions.length} prescriptions found</p>
              </div>
            </div>
          )}
        </div>

        {patientPickerOpen && <div className="fixed inset-0 z-[90] grid place-items-center bg-black/70 px-3 py-4 backdrop-blur-md sm:px-4" onClick={() => setPatientPickerOpen(false)}><div className="flex max-h-[calc(100dvh-32px)] w-full max-w-md flex-col overflow-hidden rounded-3xl border border-border bg-background p-5 shadow-2xl sm:p-6" onClick={(event) => event.stopPropagation()}><div className="mb-4 flex shrink-0 items-start justify-between gap-4"><div><p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Verified phone</p><h2 className="mt-2 text-2xl font-semibold text-foreground">Select a patient</h2><p className="mt-1 text-sm text-muted-foreground">Choose whose {pendingPortalAction === "reports" ? "reports" : "prescriptions"} you want to view.</p></div><button type="button" onClick={() => setPatientPickerOpen(false)} className="shrink-0 rounded-xl border border-border p-2 text-muted-foreground transition hover:bg-muted"><X className="h-4 w-4" /></button></div><div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">{portalPatients.map((patient) => <button key={patient.id} type="button" onClick={() => setSelectedPortalPatient(patient)} className={`flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition ${selectedPortalPatient?.id === patient.id ? "border-primary bg-primary/10" : "border-border bg-muted/20 hover:border-primary/50"}`}><span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><UserRound className="h-5 w-5" /></span><span className="min-w-0"><strong className="block truncate text-sm text-foreground">{patient.patientName || "Unnamed patient"}</strong><span className="text-xs text-muted-foreground">{[patient.age ? `${patient.age} yrs` : null, patient.gender].filter(Boolean).join(" · ") || "Patient profile"}</span></span><span className={`ml-auto h-4 w-4 shrink-0 rounded-full border-2 ${selectedPortalPatient?.id === patient.id ? "border-primary bg-primary ring-2 ring-primary/20" : "border-border"}`} /></button>)}</div><div className="shrink-0 border-t border-border pt-4"><button type="button" onClick={continuePatientAction} disabled={!selectedPortalPatient || loading || reportsLoading} className="inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-45">{loading || reportsLoading ? "Loading..." : `Continue to ${pendingPortalAction === "reports" ? "reports" : "prescriptions"}`}</button></div></div></div>}

        {createPortal(
          <AnimatePresence>
            {showModal && modalRx && (
              <motion.div
                key="rx-modal"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                onClick={() => setShowModal(false)}
                onKeyDown={(e) => e.key === "Escape" && setShowModal(false)}
                data-lenis-prevent
                className="fixed inset-0 z-[100] grid place-items-center bg-black/70 backdrop-blur-md px-4 py-6"
              >
                <motion.div
                  onClick={(e) => e.stopPropagation()}
                  initial={{ opacity: 0, y: 24, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 24, scale: 0.96 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="glass gradient-border w-full max-w-2xl rounded-3xl overflow-hidden max-h-[90vh] flex flex-col shadow-2xl"
                >
                  {/* Header — aurora */}
                  <div className="aurora-gradient p-6 sm:p-8 text-white relative">
                    <button
                      onClick={() => setShowModal(false)}
                      aria-label="Close"
                      className="absolute top-4 right-4 h-9 w-9 grid place-items-center rounded-full bg-white/15 hover:bg-white/25 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <p className="text-[11px] uppercase tracking-[0.25em] text-white/75 font-mono-cq">
                      Prescription {modalRx.id}
                    </p>
                    <h3 className="mt-2 font-display text-2xl sm:text-3xl font-semibold pr-10">
                      {modalRx.diagnosis || "Prescription details"}
                    </h3>
                    <div className="mt-5 grid gap-3 sm:grid-cols-2 text-sm text-white/85">
                      <div className="inline-flex items-center gap-2">
                        <Stethoscope className="h-4 w-4" /> {resolveDoctorName(modalRx)}
                      </div>
                      <div className="inline-flex items-center gap-2">
                        <FileText className="h-4 w-4" /> {resolvePatientInfo(modalRx)}
                      </div>
                      {(modalRx.date || modalRx.createdAt) && (
                        <div className="inline-flex items-center gap-2">
                          <CalendarDays className="h-4 w-4" />
                          {new Date(modalRx.date || modalRx.createdAt).toLocaleDateString("en-IN", {
                            day: "numeric", month: "short", year: "numeric",
                          })}
                        </div>
                      )}
                      {modalRx.status && (
                        <div className="flex items-center justify-end sm:justify-start">
                          <StatusBadge status={modalRx.status} />
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => downloadPrescription(modalRx.id)}
                      disabled={loading}
                      className="mt-6 inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 disabled:opacity-60 transition-colors text-white font-medium px-5 py-2.5 rounded-full backdrop-blur"
                    >
                      <Download className="h-4 w-4" /> {loading ? "Preparing…" : "Download prescription"}
                    </button>
                  </div>

                  {/* Body */}
                  <div className="p-6 sm:p-8 space-y-8 overflow-y-auto">
                    {/* Medicines */}
                    {Array.isArray(modalRx.medicines) && modalRx.medicines.length > 0 && (
                      <section>
                        <h4 className="font-display text-xl text-foreground flex items-center gap-2">
                          <Pill className="h-5 w-5 text-primary" /> Medicines
                        </h4>
                        <div className="mt-4 divide-y divide-border border border-border rounded-2xl overflow-hidden">
                          {modalRx.medicines.map((m, i) => (
                            <div key={i} className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-4 hover:bg-foreground/[0.03] transition-colors">
                              <div className="col-span-2 sm:col-span-1 font-medium text-foreground">
                                {m.name || m.medicineName || "—"}
                              </div>
                              <div className="text-sm text-muted-foreground">{m.dose || m.dosage || "—"}</div>
                              <div className="text-sm text-muted-foreground">{m.freq || m.frequency || "—"}</div>
                              <div className="text-sm text-muted-foreground">{m.days || m.duration || "—"}</div>
                            </div>
                          ))}
                        </div>
                      </section>
                    )}

                    {/* Diagnosis */}
                    {modalRx.diagnosis && (
                      <section>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground/70 font-mono-cq">Diagnosis</p>
                        <p className="mt-2 text-foreground leading-relaxed">{modalRx.diagnosis}</p>
                      </section>
                    )}

                    {/* Advice */}
                    <section className="rounded-2xl bg-muted/50 border border-border p-5 sm:p-6">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground/70 font-mono-cq">
                        Doctor&apos;s advice
                      </p>
                      <p className="mt-2 text-foreground/90 leading-relaxed">
                        {modalRx.advice || modalRx.notes || "No additional advice recorded."}
                      </p>
                    </section>

                    {/* Image preview if any */}
                    {modalRx.imageUrl && (
                      <section>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground/70 font-mono-cq">Prescription copy</p>
                        <a href={modalRx.imageUrl} target="_blank" rel="noreferrer"
                          className="mt-3 inline-flex items-center justify-center gap-2 rounded-2xl border border-border bg-muted/40 px-5 py-3 text-sm font-medium text-white transition hover:border-primary/70 hover:bg-primary/10">
                          <FileText className="h-4 w-4" /> View prescription
                        </a>
                      </section>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
      </div>
    </div>
  );
}